import { computed, inject, Service, signal } from '@angular/core';
import {
  codetableListToOptions,
  getPartnerInfoAsString,
  hasError,
  ILaborProtokoll,
  ILaborProtokollDaten,
  ILaborProtokollParams,
  ILaborProtokollStatusWechsel,
  ILaborProtokollZuordnungAenderung,
  STATUS_UNBEHANDELT
} from '@mng/mgs-lib';
import { MgsWebApi } from '../../mgs-webapi.service';
import { firstValueFrom } from 'rxjs';
import { CodetableService } from '../../codetable/codetable.service';
import { getFormattedDateString, updateByPredicate } from '@mng/mng-ts-helpers';
import { AuthStateFacade } from '../../auth/auth-state.facade';
import { DienstleistungService } from '../../dienstleistung/dienstleistung.service';

@Service()
export class LaborProtokollService {
  readonly codetableService = inject(CodetableService);

  readonly dienstleistungService = inject(DienstleistungService);

  private params = signal<ILaborProtokollParams>(undefined);
  protokollDaten = signal<ILaborProtokollDaten>(undefined);

  protokollDetail = signal<ILaborProtokoll>(undefined);

  readonly authState = inject(AuthStateFacade);

  protokollBearbeitbar = computed(
    () =>
      STATUS_UNBEHANDELT === this.protokollDetail()?.status.id &&
      this.protokollDetail()?.dienstleistungsCode &&
      (this.authState.isArzt() || this.authState.isAdmin())
  );

  protokollZuordenbar = computed(
    () =>
      STATUS_UNBEHANDELT === this.protokollDetail()?.status.id && (this.authState.isArzt() || this.authState.isAdmin())
  );

  protokolle = computed(() => (this.protokollDaten() ? this.protokollDaten().protokolle : []));

  readonly webApi = inject(MgsWebApi);

  statiOptions = computed(() => codetableListToOptions(this.codetableService.laborProtokollStati(), true));

  paramsAsHtmlString = computed(() => this.ermittleSuchParameterInfoAlsHtmlString());

  async laborProtokolleErmitteln(params?: ILaborProtokollParams) {
    if (params) {
      this.params.set(params);
    }

    const response = await firstValueFrom(this.webApi.laborProtokolle(params));
    this.protokollDaten.set(response.data.result);
  }

  async laborProtokoll(id: number): Promise<ILaborProtokoll> {
    const protokoll = (await firstValueFrom(this.webApi.laborProtokoll(id))).data.result;
    this.protokollDetail.set(protokoll);
    return protokoll;
  }

  laborProtokollParams(): ILaborProtokollParams {
    return this.params() ?? { statusId: STATUS_UNBEHANDELT, limit: 100 };
  }

  async laborProtokolleErmittelnWennNotwendig(params: ILaborProtokollParams = {}) {
    if (this.authState.isAdmin() && !params.partnerId) {
      return;
    }

    const notwendig =
      !this.authState.isAdmin() ||
      (this.authState.isAdmin() && params?.partnerId && params.partnerId !== this.params()?.partnerId);
    if (notwendig) {
      void (await this.laborProtokolleErmitteln(params));
    }
  }

  async laborProtokollBearbeitung(status: number) {
    const daten: ILaborProtokollStatusWechsel = {
      laborProtokollId: this.protokollDetail().id,
      status
    };
    await firstValueFrom(this.webApi.laborProtokollBearbeitung(daten));

    this.dienstleistungService.resetWennAktiv(this.protokollDetail().dienstleistungsId);
  }

  async laborProtokollZuordnungAenderung(code: string) {
    const aenderung: ILaborProtokollZuordnungAenderung = {
      laborProtokollId: this.protokollDetail().id,
      code
    };
    const response = await firstValueFrom(this.webApi.laborProtokollZuordnungAenderung(aenderung));

    if (!hasError(response)) {
      this.protokollDetail.set(response.data.result);

      if (this.protokollDaten()?.protokolle) {
        this.protokollDaten.update((daten) => ({
          ...daten,
          protokolle: updateByPredicate(
            daten.protokolle,
            (protokoll) => this.protokollDetail().id === protokoll.id,
            this.protokollDetail()
          )
        }));
      }
    }
    return response;
  }

  private ermittleSuchParameterInfoAlsHtmlString(): string {
    let out = 'Laborprotokolle ';

    if (!this.params()) {
      out += 'Keine Parameter definiert';
    } else {
      if (this.params().code) {
        out += `für Dienstleistungen mit Code <strong>${this.params().code}</strong>`;
      } else {
        if (this.params().statusId) {
          const laborStatus =
            this.statiOptions().find((status) => status.value === +this.params().statusId)?.name ?? '??';
          out += `im Status <strong>${laborStatus}</strong>`;
        }
        if (this.protokollDaten()?.partner) {
          out += ` für <strong>${getPartnerInfoAsString(this.protokollDaten().partner, true)}</strong>`;
        }
      }

      if (this.params().vonDatum) {
        out += ' ab' + getFormattedDateString(this.params().vonDatum);
      }

      if (this.params().bisDatum) {
        out += ' bis ' + getFormattedDateString(this.params().bisDatum);
      }
    }

    if (this.protokolle().length === 0) {
      out += ', keine Treffer';
    } else {
      out += `, ${this.protokolle().length} Treffer`;
    }

    if (this.protokolle().length >= this.params()?.limit) {
      out += ` (Anzeigelimit von ${this.params().limit})`;
    }
    return out;
  }
}
